import React, { useEffect, useRef, useState } from 'react';
import { Badge, Button, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '../Common/Modal/Alert';
import * as aiConversationsActions from '../../store/aiConversations/actions';
import {
  canBeStopped,
  CONVERSATION_STATUS,
  ENDED_REASONS,
  HANDOFF_REASONS,
  TAKE_BACK_ROLES,
} from '../../config/aiConversationStatus';
import callOutcomes from '../../config/callOutcomes';
import { selectMonetizationOrders } from '../../selectors/monetizationOrder';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const when = (date) => (date ? moment(date).format('DD/MM/YY HH:mm') : '-');

/**
 * What happened, then when and how much. The outcome leads because it is
 * what an operator opens this for; everything else is context and stays
 * quiet underneath.
 */
const Summary = ({ conversation }) => {
  const { t } = useTranslation();

  // What the agent proposed and no engine applied: the offers the lead
  // accepted, the causale and the prose the agent wrote. The conversation has
  // carried this since closePositive's proposal branch existed and NOTHING
  // rendered it — not here, not on the call. So the record the handoff was
  // designed around ("the operator closes the rest during the call") was one
  // an operator could not read.
  const proposed = conversation.proposedClosure;
  const proposedIds = (proposed && proposed.monetizationOrderIds) || [];
  // Resolved against the store FormLead already fills on the call page, where
  // it dispatches syncMonetizationOrders for every lead it renders. An id
  // that does not resolve is shown as itself rather than hidden: a proposal
  // on an order this country cannot see is a finding, not a row to drop.
  const known = useSelector(selectMonetizationOrders(proposedIds));
  const proposedOrders = proposedIds.map((id) => {
    const order = known.find((candidate) => candidate.id === id);
    return { id, name: (order && order.name) || id };
  });

  const outcome = callOutcomes[conversation.outcome];
  const status = CONVERSATION_STATUS[conversation.status];
  const reason = HANDOFF_REASONS[conversation.handoffReason];

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap">
        {outcome ? (
          <Badge color={outcome.color} className="font-size-12">
            <i className={`${outcome.icon} align-middle mr-1`}></i>
            {t(outcome.title)}
          </Badge>
        ) : (
          <Badge color="secondary" className="font-size-12">
            {t('No outcome')}
          </Badge>
        )}
        <Badge color={status ? status.color : 'light'} className="ml-1">
          {status ? t(status.title) : conversation.status}
        </Badge>
        {reason && (
          <Badge color="light" className="ml-1">
            {t(reason)}
          </Badge>
        )}
        {conversation.stoppedAt && (
          <Badge color="dark" className="ml-1">
            {t(
              conversation.endedReason
                ? ENDED_REASONS[conversation.endedReason] ||
                    conversation.endedReason
                : 'Stopped by hand',
            )}
          </Badge>
        )}
      </div>
      <div className="text-muted font-size-12 mt-1">
        {t('Started')} {when(conversation.startedAt)}
        {conversation.endedAt &&
          ` · ${t('ended')} ${when(conversation.endedAt)}`}
        {' · '}
        {conversation.turnCount || 0} {t('turns')}
        {conversation.reacquisitions
          ? ` · ${t('taken back')} ${conversation.reacquisitions}×`
          : ''}
      </div>
      {!!proposed && (
        // `proposedAt` is deliberately not read: the transformer converts five
        // top-level timestamps and not this nested one, so it arrives as a raw
        // Timestamp and would render as [object Object]. handoffAt is the same
        // instant, converted, and already above.
        <div className="mt-2 pl-2 border-left border-danger">
          <div className="font-size-12 font-weight-bold">
            {t(
              // Once the conversation ended for a reason of its own — the
              // call center closed the lead — nobody is going to act on
              // this: it is history, and the heading must not say otherwise.
              conversation.endedReason
                ? 'What the agent proposed'
                : 'What the agent proposed, waiting for a person',
            )}
          </div>
          <div className="mt-1">
            {proposedOrders.length ? (
              proposedOrders.map((order) => (
                <Badge key={order.id} color="info" className="mr-1">
                  {order.name}
                </Badge>
              ))
            ) : (
              <span className="text-muted font-size-12">{t('None')}</span>
            )}
          </div>
          <div className="text-muted font-size-12 mt-1">
            {t('Positive reason')}:{' '}
            {proposed.reason ||
              t(
                'Not recorded: a causale is kept only when a single offer closes',
              )}
          </div>
          {!!proposed.rationale && (
            <div
              className="font-size-12 mt-1"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {proposed.rationale}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** The house chat style, from DemTemplates/AiChatPanel: ours on the right. */
const Bubble = ({ message }) => {
  const { t } = useTranslation();
  const fromAgent = message.role !== 'user';

  return (
    <div className={`mb-2 ${fromAgent ? 'text-right' : ''}`}>
      <div
        className="d-inline-block text-left px-2 py-1"
        style={{
          maxWidth: '75%',
          borderRadius: fromAgent ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
          background: fromAgent ? '#556ee6' : '#f8f8fb',
          color: fromAgent ? '#fff' : '#495057',
          border: fromAgent ? 'none' : '1px solid #e9e9ef',
          fontSize: 12,
          whiteSpace: 'pre-wrap',
        }}
      >
        {message.content}
      </div>
      <div className="text-muted font-size-10">
        {fromAgent ? t('Agent') : t('Lead')} · {when(message.at)}
      </div>
    </div>
  );
};

const AiConversations = ({ lead, onCreateExtraCall }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const loading = useSelector((state) => state.AiConversation.loading);
  const loadingMessages = useSelector(
    (state) => state.AiConversation.loadingMessages,
  );
  const conversations = useSelector(
    (state) => state.AiConversation.aiConversations,
  );
  const messages = useSelector((state) => state.AiConversation.messages);
  const error = useSelector((state) => state.AiConversation.error);
  // The callable refuses everyone else: an operator on the call screen used
  // to see the button, press it and get an error.
  const canTakeBack = useSelector((state) =>
    TAKE_BACK_ROLES.includes((state.Auth.admin || {}).role),
  );
  const messagesFailed = !!error && !loadingMessages && !messages.length;

  const [selectedId, setSelectedId] = useState(null);
  const [stopAlert, setStopAlert] = useState(false);
  const transcript = useRef(null);

  // The id, not the object. Every page that opens this panel hands it a fresh
  // one: the monitor writes `lead={{ id: openLeadId }}` inline and re-renders
  // on its own countdown and on every live snapshot, and the call page gets a
  // new lead each time it is read back. Keyed on the object, this effect reset
  // the store and re-read the conversations on each of those — and since
  // `selectedId` did not change, the messages effect below never ran again, so
  // the transcript went blank until the panel was closed and opened.
  const leadId = lead?.id;
  useEffect(() => {
    leadId &&
      dispatch(
        aiConversationsActions.fetchAiConversationsByLead({ id: leadId }),
      );
    return () => dispatch(aiConversationsActions.resetState());
  }, [leadId]);

  // The most recent one is what an operator is looking for: it is the only
  // one that can still be live.
  useEffect(() => {
    if (!conversations.length) return;
    const stillThere = conversations.some(({ id }) => id === selectedId);
    if (!stillThere) setSelectedId(conversations[0].id);
  }, [conversations]);

  useEffect(() => {
    selectedId &&
      dispatch(aiConversationsActions.fetchAiConversationMessages(selectedId));
  }, [selectedId]);

  useEffect(() => {
    if (transcript.current)
      transcript.current.scrollTop = transcript.current.scrollHeight;
  }, [messages]);

  const selected = conversations.find(({ id }) => id === selectedId);

  // A person may want to call a lead the agent turned down. Only the page
  // that passes the callback offers it: on the call screen the operator
  // already holds the call, and the monitor has no form for it. The closure
  // must be the agent's own (completed, not a handoff, which already put the
  // call in the operator queue) and still the lead's current call:
  // createExtraCall moves lead.callId onto the call it creates, so a lead
  // pointing elsewhere already has its recall.
  const canCallBack =
    !!onCreateExtraCall &&
    !!selected &&
    selected.status === 'completed' &&
    selected.outcome === 'negative' &&
    !!lead &&
    lead.callId === selected.callId;

  const handleStop = () => {
    dispatch(aiConversationsActions.stopAiConversation(selected));
    setStopAlert(false);
  };

  if (loading && !conversations.length)
    return (
      <div className="modal-body text-center py-5">
        <Spinner color="secondary" />
      </div>
    );

  // An error is not an absence: saying "none" over a failed read sends an
  // operator away convinced there is nothing to see.
  if (error && !conversations.length)
    return <div className="modal-body text-danger">{error}</div>;

  if (!conversations.length)
    return (
      <div className="modal-body text-muted">
        {t('No AI conversation for this lead')}
      </div>
    );

  return (
    <React.Fragment>
      <div className="modal-body p-0">
        {conversations.length > 1 && (
          <div className="px-3 pt-2 border-bottom">
            {conversations.map((conversation) => (
              <Button
                key={conversation.id}
                size="sm"
                color="light"
                className="mr-1 mb-2 font-size-11"
                active={conversation.id === selectedId}
                onClick={() => setSelectedId(conversation.id)}
              >
                {when(conversation.startedAt)}
              </Button>
            ))}
          </div>
        )}

        <div className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between flex-wrap">
          {selected && <Summary conversation={selected} />}
          <div>
            {canTakeBack && (
              <Button
                size="sm"
                color="danger"
                disabled={!canBeStopped(selected) || loading}
                onClick={() => setStopAlert(true)}
              >
                <i className="bx bx-stop-circle font-size-14 align-middle mr-1"></i>
                {t('Back to the call center')}
              </Button>
            )}
            {canCallBack && (
              <Button
                size="sm"
                color="primary"
                className="ml-1"
                onClick={() =>
                  onCreateExtraCall({
                    id: selected.callId,
                    acquisitionOrderId: selected.acquisitionOrderId,
                  })
                }
              >
                <i className="bx bx-phone-call font-size-14 align-middle mr-1"></i>
                {t('Create extra call')}
              </Button>
            )}
          </div>
        </div>

        {/* The transcript scrolls inside its own box: a long conversation
            must not push the modal past the bottom of the screen. */}
        <div
          ref={transcript}
          className="overflow-auto px-3 py-2"
          style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 160 }}
        >
          {messagesFailed ? (
            <p className="text-danger mb-0">{error}</p>
          ) : loadingMessages ? (
            <div className="text-center py-4">
              <Spinner color="secondary" size="sm" />
            </div>
          ) : !messages.length ? (
            <p className="text-muted mb-0">{t('No messages yet')}</p>
          ) : (
            messages.map((message) => (
              <Bubble key={message.id} message={message} />
            ))
          )}
        </div>
      </div>
      <Alert
        isOpen={stopAlert}
        title={t('Do you want to take this lead back from the AI?')}
        confirmBtnLabel={t('Take it back')}
        onCancel={() => setStopAlert(false)}
        onConfirm={handleStop}
      >
        {t(
          'The agent stops writing and the call goes back to the operator queue. If she wrote in the last 24 hours, she receives the handoff message of the acquisition order.',
        )}
      </Alert>
    </React.Fragment>
  );
};

export default AiConversations;
